import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CalendarCheck, Check, Clock, MapPin, MessageCircle, Sparkles, Users } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { SERVICES, OCCASIONS, TAGLINE, waLink, DEFAULT_WA_TEXT } from "@/lib/data";

const inputClass =
  "w-full rounded-xl border-2 border-gold/40 bg-cream px-4 py-3.5 font-semibold text-cocoa placeholder:text-rose/60 outline-none transition-colors duration-300 focus:border-brand";
const labelClass = "mb-2 block text-xs font-extrabold uppercase tracking-[0.2em] text-brand";

const Field = ({ label, children }) => (
  <div>
    <label className={labelClass}>{label}</label>
    {children}
  </div>
);

const BookingForm = () => {
  const [params] = useSearchParams();
  const prefill = params.get("service");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    event_type: "particulier",
    occasion: "",
    service: prefill && (SERVICES.some((s) => s.slug === prefill) || prefill === "op-maat")
      ? (SERVICES.find((s) => s.slug === prefill)?.title || "Iets anders op maat")
      : "",
    date: "",
    location: "",
    guests: 100,
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.includes("@") || !form.service) {
      setError("Oeps! Vul naam, e-mailadres en dienst in.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("mislukt");
      setDone(true);
    } catch {
      setError("Oeps, daar ging iets mis. Probeer opnieuw of stuur ons een WhatsAppje!");
    } finally {
      setSending(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
        className="card-glow rounded-3xl border-2 border-gold/50 bg-white p-10 text-center"
        data-testid="booking-success"
      >
        <motion.span
          initial={{ rotate: -20, scale: 0 }}
          animate={{ rotate: 0, scale: 1 }}
          transition={{ type: "spring", stiffness: 260, damping: 14, delay: 0.2 }}
          className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gold text-brand-dark"
        >
          <Check size={30} />
        </motion.span>
        <h3 className="mt-6 font-display text-3xl font-black text-cocoa">Hoera, ontvangen!</h3>
        <p className="mx-auto mt-3 max-w-sm font-semibold text-rose">
          Bedankt {form.name.split(" ")[0]}! We nemen binnen één werkdag contact op met een voorstel
          waar jouw gasten van zullen juichen. Spoed? App ons gerust!
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <a
            href={waLink(`Hallo! Ik heb net een aanvraag gedaan via de website (${form.service}).`)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="success-whatsapp"
            className="joy-hover flex items-center gap-2 rounded-full bg-whatsapp px-6 py-3 text-sm font-black uppercase tracking-wider text-white"
          >
            <MessageCircle size={15} /> WhatsApp
          </a>
          <Link
            to="/"
            data-testid="success-home"
            className="joy-hover rounded-full border-2 border-brand/40 px-6 py-3 text-sm font-extrabold uppercase tracking-wider text-brand hover:bg-brand hover:text-white"
          >
            Terug naar home
          </Link>
        </div>
      </motion.div>
    );
  }

  const chipClass = (active) =>
    `joy-hover rounded-full border-2 px-4 py-2 text-sm font-extrabold ${
      active ? "border-brand bg-brand text-white" : "border-brand/30 text-brand hover:border-brand"
    }`;

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="booking-form">
      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Type event">
          <div className="flex gap-2" data-testid="event-type-toggle">
            {[
              { key: "zakelijk", label: "Zakelijk" },
              { key: "particulier", label: "Privé" },
            ].map((t) => (
              <button
                type="button"
                key={t.key}
                data-testid={`event-type-${t.key}`}
                onClick={() => set("event_type", t.key)}
                className={`joy-hover flex-1 rounded-xl border-2 px-4 py-3 text-sm font-extrabold ${
                  form.event_type === t.key
                    ? "border-brand bg-brand text-white"
                    : "border-brand/30 text-brand hover:border-brand"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </Field>
        <Field label="Dienst">
          <select
            value={form.service}
            onChange={(e) => set("service", e.target.value)}
            data-testid="form-service"
            className={inputClass}
          >
            <option value="">Kies een dienst…</option>
            {SERVICES.map((s) => (
              <option key={s.slug} value={s.title} className="bg-cream">
                {s.title}
              </option>
            ))}
            <option value="Iets anders op maat" className="bg-cream">
              Iets anders op maat
            </option>
          </select>
        </Field>
      </div>

      <Field label="Gelegenheid">
        <div className="flex flex-wrap gap-2" data-testid="occasion-chips">
          {OCCASIONS.map((o) => (
            <button
              type="button"
              key={o}
              data-testid={`occasion-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => set("occasion", form.occasion === o ? "" : o)}
              className={chipClass(form.occasion === o)}
            >
              {o}
            </button>
          ))}
        </div>
      </Field>

      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Naam *">
          <input
            type="text"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Jouw naam"
            data-testid="form-name"
            className={inputClass}
          />
        </Field>
        <Field label="E-mail *">
          <input
            type="email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            placeholder="jij@voorbeeld.be"
            data-testid="form-email"
            className={inputClass}
          />
        </Field>
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <Field label="Telefoon">
          <input
            type="tel"
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            placeholder="+32 4XX XX XX XX"
            data-testid="form-phone"
            className={inputClass}
          />
        </Field>
        <Field label="Gewenste datum">
          <input
            type="date"
            value={form.date}
            onChange={(e) => set("date", e.target.value)}
            data-testid="form-date"
            className={`${inputClass} [color-scheme:light]`}
          />
        </Field>
      </div>

      <Field label="Locatie van het feest">
        <input
          type="text"
          value={form.location}
          onChange={(e) => set("location", e.target.value)}
          placeholder="Plaats, zaal of adres"
          data-testid="form-location"
          className={inputClass}
        />
      </Field>

      <Field label={`Verwacht aantal gasten: ${form.guests}`}>
        <input
          type="range"
          min="10"
          max="1000"
          step="5"
          value={form.guests}
          onChange={(e) => set("guests", Number(e.target.value))}
          data-testid="form-guests-slider"
          className="mt-2 w-full accent-[#D0121B]"
        />
      </Field>

      <Field label="Bericht">
        <textarea
          rows="4"
          value={form.message}
          onChange={(e) => set("message", e.target.value)}
          placeholder="Vertel ons kort iets over je feest…"
          data-testid="form-message"
          className={`${inputClass} resize-none`}
        />
      </Field>

      {error && (
        <p className="rounded-xl border-2 border-red-300 bg-red-50 px-4 py-3 text-sm font-bold text-red-600" data-testid="form-error">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={sending}
        data-testid="booking-form-submit"
        className="joy-hover flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 text-sm font-black uppercase tracking-wider text-brand-dark disabled:opacity-60"
      >
        {sending ? "Toveren…" : "Verstuur mijn feestje!"}
        {!sending && <ArrowRight size={16} />}
      </button>
      <p className="text-center text-xs font-bold text-rose">Vrijblijvend en met een glimlach — antwoord binnen één werkdag!</p>
    </form>
  );
};

const Contact = () => (
  <>
      <Seo
        title="Show boeken? Offerte op maat | entertainmentshows.be"
        description="Vertel ons over je feest en ontvang binnen één werkdag een voorstel met prijs. Boek via het formulier of direct via WhatsApp."
        path="/contact"
      />
    <section className="relative overflow-hidden pb-14 pt-40" data-testid="contact-hero">
      <div className="brand-glow pointer-events-none absolute inset-0" />
      <div className="confetti-dots pointer-events-none absolute inset-0 opacity-70" />
      <div className="relative mx-auto max-w-7xl px-6">
        <p className="flex items-center gap-3 text-xs font-extrabold uppercase tracking-[0.35em] text-gold-light">
          <Sparkles size={14} />
          Contact & boeking
        </p>
        <MaskedLines
          as="h1"
          lines={["Zeg het voort:", <><em className="gold-text italic">feestje</em> boeken!</>]}
          className="mt-5 block font-display text-4xl font-black leading-[1.05] tracking-tight text-snow sm:text-5xl lg:text-6xl"
        />
        <Reveal delay={0.3} y={16}>
          <p className="mt-4 font-script text-3xl text-gold-light">{TAGLINE}</p>
          <p className="mt-4 max-w-xl text-lg font-semibold leading-relaxed text-blush">
            Vertel ons over je feest en je krijgt binnen één werkdag antwoord met een glimlach.
            Liever direct appen? Ook goed!
          </p>
        </Reveal>
      </div>
    </section>

    <section className="bg-cream pb-28" data-testid="contact-content">
      <div className="mx-auto grid max-w-7xl gap-10 px-6 py-16 lg:grid-cols-[1.15fr_0.85fr]">
        <Reveal>
          <div className="card-glow rounded-3xl border-2 border-gold/50 bg-white p-8 lg:p-10">
            <BookingForm />
          </div>
        </Reveal>

        <div className="space-y-6">
          <Reveal delay={0.1}>
            <a
              href={waLink(DEFAULT_WA_TEXT)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-card"
              className="group block rounded-3xl border-2 border-whatsapp/50 bg-whatsapp/10 p-8 transition-all duration-300 hover:bg-whatsapp/20"
            >
              <div className="flex items-center justify-between">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-whatsapp text-white">
                  <MessageCircle size={24} />
                </span>
                <ArrowRight size={20} className="text-whatsapp transition-transform duration-300 group-hover:translate-x-1" />
              </div>
              <h3 className="mt-5 font-display text-2xl font-black text-cocoa">Direct WhatsApp!</h3>
              <p className="mt-2 font-semibold text-rose">
                Snelste route: stuur ons een berichtje. Meestal antwoorden we binnen
                <span className="font-black text-green-700"> 30 minuten!</span>
              </p>
            </a>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="card-glow rounded-3xl border-2 border-gold/50 bg-white p-8" data-testid="contact-steps">
              <p className="text-xs font-extrabold uppercase tracking-[0.25em] text-gold-deep">Zo gaat het verder</p>
              <ol className="mt-6 space-y-6">
                {[
                  { icon: CalendarCheck, titel: "1. Jij roept: feestje!", tekst: "Je stuurt je aanvraag in — wij antwoorden binnen één werkdag met een voorstel (en prijs)." },
                  { icon: Clock, titel: "2. We stemmen af!", tekst: "Show op maat van je publiek, locatie en planning. Al jouw wensen erin." },
                  { icon: Sparkles, titel: "3. Showtime!", tekst: "Wij komen volledig ontzorgd ter plaatse. Jij hoeft alleen maar te juichen." },
                ].map((s) => (
                  <li key={s.titel} className="flex gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-2 border-brand/40 text-brand">
                      <s.icon size={17} />
                    </span>
                    <div>
                      <p className="font-black text-cocoa">{s.titel}</p>
                      <p className="mt-1 text-sm font-semibold text-rose">{s.tekst}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </Reveal>

          <Reveal delay={0.3}>
            <div className="rounded-3xl border-2 border-gold/50 bg-brand p-8" data-testid="contact-coverage">
              <div className="flex items-center gap-3">
                <MapPin size={18} className="text-gold-light" />
                <p className="font-black text-white">Speelgebied</p>
              </div>
              <p className="mt-3 text-sm font-semibold leading-relaxed text-blush">
                Heel België en Nederland! Verder weg? Vraag het gewoon — we komen er graag.
              </p>
              <p className="mt-4 flex items-center gap-2 text-sm font-bold text-gold-light">
                <Users size={14} /> Van 10 gasten tot 1000+ toeschouwers
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  </>
);

export default Contact;
